import React from 'react';
import { X, Star, Users, ExternalLink, Play, Heart, Share2, CheckCircle, XCircle, Calendar } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bot } from '@/types/Bot';
import { UserReviews } from '@/components/UserReviews';

interface BotProfileModalProps {
  bot: Bot | null;
  isOpen: boolean;
  onClose: () => void;
  onToggleFavorite: (botId: string) => void;
  isFavorite: boolean;
}

export const BotProfileModal: React.FC<BotProfileModalProps> = ({
  bot,
  isOpen,
  onClose,
  onToggleFavorite,
  isFavorite
}) => {
  if (!bot) return null;

  const getPriceColor = (price: string) => {
    switch (price) {
      case 'Free': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'Freemium': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'Paid': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-card border-border">
        <div className="relative">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-foreground">{bot.name}</h1>
                {bot.isTrending && (
                  <Badge className="bg-primary text-primary-foreground">
                    🔥 Trending
                  </Badge>
                )}
                {bot.isNew && (
                  <Badge variant="outline" className="border-green-500 text-green-400">
                    New
                  </Badge>
                )}
              </div>
              
              <div className="flex items-center gap-4 mb-3">
                <Badge variant="outline" className={getPriceColor(bot.price)}>
                  {bot.price}
                </Badge>
                <Badge variant="secondary" className="capitalize">
                  {bot.category}
                </Badge>
                <div className="flex items-center gap-1 text-sm">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-medium">{bot.rating}</span>
                  <span className="text-muted-foreground">({bot.reviewCount.toLocaleString()} reviews)</span>
                </div>
              </div>

              <p className="text-muted-foreground text-lg leading-relaxed">
                {bot.longDescription || bot.description}
              </p>
            </div>

            <div className="flex gap-2 ml-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onToggleFavorite(bot.id)}
                className={isFavorite ? "text-red-500 border-red-300" : ""}
              >
                <Heart className={`w-4 h-4 ${isFavorite ? 'fill-red-500' : ''}`} />
              </Button>
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Screenshots/Demo */}
          {(bot.screenshots || bot.videoUrl || bot.imageUrl) && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-3">Preview</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {bot.videoUrl && (
                  <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <Play className="w-12 h-12 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Demo Video Available</p>
                    </div>
                  </div>
                )}
                <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                  <img 
                    src={bot.imageUrl} 
                    alt={bot.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Features & Use Cases */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Key Features</h3>
              <div className="space-y-2">
                {bot.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {bot.useCases && (
              <div>
                <h3 className="text-lg font-semibold mb-3">Use Cases</h3>
                <div className="space-y-2">
                  {bot.useCases.map((useCase, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2" />
                      <span className="text-sm">{useCase}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Pros & Cons */}
          {(bot.pros || bot.cons) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {bot.pros && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-green-400">Pros</h3>
                  <div className="space-y-2">
                    {bot.pros.map((pro, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
                        <span className="text-sm">{pro}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {bot.cons && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-red-400">Considerations</h3>
                  <div className="space-y-2">
                    {bot.cons.map((con, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-red-400 mt-0.5" />
                        <span className="text-sm">{con}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tags */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {bot.tags.map((tag, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>

          {/* User Reviews Section */}
          <div className="mb-6 pt-4 border-t border-border">
            <UserReviews botId={bot.id} botName={bot.name} />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4 border-t border-border">
            {bot.demoUrl && (
              <Button variant="outline" className="flex-1" onClick={() => window.open(bot.demoUrl, '_blank')}>
                <Play className="w-4 h-4 mr-2" />
                Try Demo
              </Button>
            )}
            <Button 
              className="flex-1 bg-primary " 
              onClick={() => window.open(bot.website, '_blank')}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Visit Website
            </Button>
          </div>

          {/* Last Updated */}
          {bot.lastUpdated && (
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
              <Calendar className="w-3 h-3" />
              <span>Last updated: {bot.lastUpdated}</span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};